import moment from "moment";

const CACHE_KEY = "DEKO-X-CACHE";
const EXPIRE_DAYS = 7;

const memoryStore = new Map<string, unknown>();

// CacheStorage is exposed only in secure contexts, so it is absent when the app is served over plain
// http from a non-localhost host. Degrade to the in-memory store instead of throwing a ReferenceError.
const cacheStorage: CacheStorage | undefined = typeof caches === "undefined" ? undefined : caches;

export const cacheCleanup = async () => {
  if (!cacheStorage) return;

  const cache = await cacheStorage.open(CACHE_KEY);
  const requests = await cache.keys();

  requests.forEach(request => {
    cache.match(request).then(response => {
      if (response) {
        const timestamp = response.headers.get("x-cache-timestamp");
        if (moment().diff(moment(timestamp), "day") > EXPIRE_DAYS) {
          cache.delete(request);
        }
      }
    });
  });
};

export const deleteAllCache = async () => {
  memoryStore.clear();
  if (!cacheStorage) return;

  await cacheStorage.delete(CACHE_KEY);
};

export const deleteCache = async (key: string) => {
  memoryStore.delete(key);
  if (!cacheStorage) return;

  const cache = await cacheStorage.open(CACHE_KEY);
  cache.delete(key);
};

export const saveCache = async (key: string, data: unknown, options?: { memory?: boolean }) => {
  if (options?.memory || !cacheStorage) {
    memoryStore.set(key, data);
    return;
  }

  try {
    const cache = await cacheStorage.open(CACHE_KEY);

    const response = new Response(JSON.stringify(data), {
      status: 200,
      statusText: "OK",
      headers: {
        "Content-Type": "application/json",
        "x-cache-timestamp": moment().toISOString()
      }
    });

    await cache.put(key, response);
  } catch (error) {
    deleteAllCache();
  }
};

export const getCache = async (key: string) => {
  if (memoryStore.has(key)) {
    const data = memoryStore.get(key);
    return new Response(JSON.stringify(data), {
      status: 200,
      statusText: "OK",
      headers: { "Content-Type": "application/json" }
    });
  }

  if (!cacheStorage) return undefined;

  const cache = await cacheStorage.open(CACHE_KEY);
  return cache.match(key);
};
